import { Log } from '@artup/utilities'
import { isRouteNotFoundNavigationFailure, isNonCriticalNavigationFailure, getNavigationFailure, InitializingRoute } from '../../router'

export default {
  /**
   * Initialize the router
   * @param {Router} router UI router instance
   */
  async initialize (router) {
    this.router = router
    this.routes = []
    this.currentRoute = null
    this.previousRoute = null

    router.beforeEach((to, from) => {
      // If initializing, remember the initial route to load
      // as it's probably not available yet
      if (this.routes.length === 0) {
        this.initialRoute = to
        return false
      }

      this.navigating.from = from
      this.navigating.to = to
      this.navigating.startedAt = Date.now()
      this.navigating.endedAt = null
    })

    router.afterEach((to, from) => {
      this.previousRoute = from
      this.currentRoute = to
      this.navigating.endedAt = Date.now()
    })
  },

  /**
   * Adds routes to the router
   * @param {Array[Route]} routes UI routes
   */
  async addRoutes (routes) {
    // Retire the placeholder route which stood in until now
    if (this.router.hasRoute(InitializingRoute)) {
      this.router.removeRoute(InitializingRoute)
    }

    this.routes = [...this.routes, ...routes]
    for (const route of routes) {
      this.router.addRoute(route)
    }
  },

  /**
   * Loads the initial route and starts navigation
   */
  async start () {
    const { router, initialRoute } = this
    // The initial route was resolved against the placeholder route which no longer exists,
    // so navigate by its location rather than by the resolved route record
    router.push(initialRoute ? initialRoute.fullPath : { path: '/' })
  },

  /**
   * Navigates to the specified route
   * @param {String} name Route to navigate to
   * @param {String} path Alternatively, a path to navigate to
   * @param {String} fail Route to navigate to, if error happens
   * @param {Object} params Route parameters
   * @param {Object} query Route query parameters
   */
  async gotoRoute ({ name, path, fail = {}, params = {}, query = {} }) {
    if (!(name || path)) throw new Error('Route name or path is required')
    const { router } = this
    if (!router) throw new Error('Router is not initialized')

    try {
      if (name) {
        // either by name
        await router.push({ name, params, query })
      } else {
        // or by path
        await router.push({ path, params, query })
      }
    } catch (error) {
      // If error, navigate to the fallback route if specified
      const { name, path } = fail
      if (name || path) {
        if (name) {
          await router.push({ name })
        } else {
          await router.push({ path })
        }
      } else {
        // If route not found, just go to main route
        if (isRouteNotFoundNavigationFailure(error)) {
          Log.warn(`Route ${name || path} not found`)
          await router.push({ path: '/' })
        } else {
          // Skip those pesky reduntant or cancelled navigation errors, we don't really care
          if (isNonCriticalNavigationFailure(error)) {
            const { name, from, to, type } = getNavigationFailure(error)
            Log.warn(`Navigation ${name || 'redirected'}`, `${from.path} => ${to.path} ${type ? `[${type}]` : ''}`)
          } else {
            throw error
          }
        }
      }
    }
  },

  /**
   * Updates the current route with specified params and/or query
   * @param {Object} params Route parameters to override
   * @param {Object} query Query parameters to override
   */
  async updateRoute ({ params: newParams = {}, query: newQuery = {} }) {
    const { router } = this
    const { currentRoute } = router

    // Add new parameters and query values
    const params = { ...currentRoute.params, ...newParams }
    const query = { ...currentRoute.query, ...newQuery }

    // Remove parameters and query values which were set to null/undefined
    for (const [key, value] of Object.entries(newParams)) {
      if (value == null) {
        delete params[key]
      }
    }
    for (const [key, value] of Object.entries(newQuery)) {
      if (value == null) {
        delete query[key]
      }
    }

    // Update the route
    const updatedRoute = { ...currentRoute, params, query }
    await router.push(updatedRoute)
  },

  /**
   * Reloads the current route
   */
  reloadRoute () {
    const { router } = this
    router.go(router.currentRoute)
  },

  /**
   * Navigates to the previous route.
   * If previous route is login page, navigates
   * to the specified fallback route instead.
   * @param {Route} fallback Fallback route
   */
  async goBack ({ fallback } = {}) {
    const { router, previousRoute } = this
    if (router) {
      if (previousRoute.name === 'login') {
        if (fallback) {
          await this.gotoRoute(fallback)
        } else {
          router.push('/')
        }
      } else {
        if (window.history.length > 1) {
          router.back()
        } else {
          router.push('/')
        }
      }
    }
  },

  /**
   * Navigates to the specified URL
   * @param {String} url Url to navigate to
   */
  gotoUrl ({ url }) {
    if (!url) throw new Error('URL is required')
    window.location = url
  },

  /**
   * Navigates to blank URL
   */
  gotoBlank () {
    window.location = 'about:blank'
  },
}
